import { Loader2 } from 'lucide-react'
import { useLocale } from '../i18n/context'
import type { FillBatchStatusResponse } from '../hooks/useFillAll'

interface Props {
  status: FillBatchStatusResponse
  stopping: boolean
  onStop: () => void
}

// Slim in-feed progress line (Prompt 8, frame "Fill all — in progress").
// Deliberately not a modal or overlay — "the feed stays fully usable
// including the composer" — this is the entire footprint of a running batch.
export default function FillAllProgressBar({ status, stopping, onStop }: Props) {
  const { tr } = useLocale()
  const dm = tr.deckManagement

  const total = status.dictionary_total + status.ai_total
  const done = status.dictionary_filled + status.ai_filled + status.ai_failed
  const pct = total > 0 ? Math.min(100, (done / total) * 100) : 0
  const running = status.status === 'running'
  const inAiPhase = status.dictionary_filled >= status.dictionary_total && status.ai_total > 0

  const statusLine = running
    ? (stopping ? dm.fillAllStopping : dm.fillAllProgress(done, total))
    : status.status === 'done' ? dm.fillAllDone
      : status.status === 'stopped' ? dm.fillAllStopped
        : dm.fillAllFailed

  return (
    <div style={{ padding: '0 16px 11px', display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {running && !stopping && (
          <Loader2 size={13} color="var(--accent)" style={{ animation: 'fillAllSpin 1s linear infinite' }} />
        )}
        <span style={{ fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>
          {statusLine}
        </span>
        <div style={{ flex: 1 }} />
        {running && (
          <button
            onClick={onStop}
            disabled={stopping}
            style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 12, cursor: stopping ? 'default' : 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            {dm.fillAllStopBtn}
          </button>
        )}
      </div>
      <div style={{ height: 4, borderRadius: 'var(--radius-pill)', background: 'var(--bg-secondary)', overflow: 'hidden', display: 'flex' }}>
        <span style={{ width: `${pct}%`, background: 'var(--accent)', transition: 'width .2s' }} />
      </div>
      {running && !inAiPhase && status.ai_total > 0 && (
        <div style={{ fontFamily: 'var(--font-ui)', fontSize: 11, color: 'var(--text-secondary)' }}>
          {dm.fillAllDictionaryThenAi(status.dictionary_filled)}
        </div>
      )}
      <style>{'@keyframes fillAllSpin { to { transform: rotate(360deg) } }'}</style>
    </div>
  )
}
